// Pure shaping for the wiki sidebar: how the runner's flat path list becomes a
// tree, and how a commit stamp becomes an age. Same arrangement as sessions.ts —
// it lives here rather than in the component so `pnpm test` can cover it without
// rendering anything.

import type { WikiEntry } from './runner';

/*
 * The wiki workspace used to be a constant here — `WIKI_WORKSPACE = 'fian'` —
 * read by both the route handler and the browser. It is now a property of the
 * signed-in user, because the two people using this console keep two separate
 * organisations' state and neither wiki is the other's.
 *
 * The server reads it off the registry entry; the browser gets it from
 * /api/me. Nothing about the shaping below depends on which repo it came out
 * of, which is why this file needed nothing else.
 */

/**
 * The second shelf: standing questions and the sweeps that answer them.
 *
 * A constant rather than a per-user field, which is the opposite of the wiki
 * above and is the honest reflection of what this is. A wiki is somebody's
 * organisation and has to be named per person; research is one repo, on this
 * machine, holding work that has not earned a place in any wiki yet. When a
 * second person needs their own, this becomes a registry field and the rest of
 * the code does not move — the name is threaded through as a value everywhere it
 * is used, never assumed.
 *
 * It is deliberately not in the wiki. `~/fian` is what the company knows; this
 * is what it is still finding out, and most of it will never be worth keeping.
 * Promotion is a hand copy from one to the other, and that copy is the moment
 * something becomes state. The sidebar shows both so the copy is a short move
 * rather than an act of faith.
 */
export const RESEARCH_WORKSPACE = 'research';

export type TreeNode = {
  /** What the sidebar shows: the last path segment, read as a title. */
  name: string;
  /** The full path relative to the wiki root — what the file route wants back. */
  path: string;
  type: 'dir' | 'file';
  /** Bytes, on files only. */
  size?: number;
  last_commit: number | null;
  children: TreeNode[];
};

/* Words that stay lowercase inside a title, the way a copy editor would set
   them. First position always wins over this list, so `a-night-at-ashworth-manor`
   comes out as "A Night at Ashworth Manor" — capital A, small at. */
const SMALL = new Set([
  'a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'from', 'if', 'in', 'into',
  'nor', 'of', 'on', 'or', 'over', 'the', 'to', 'up', 'via', 'vs', 'with',
]);

/**
 * A path's display title.
 *
 * The rest of this app is written for someone who is not looking at a computer
 * for a living, and then the wiki put `a-night-at-ashworth-manor.md` in front of
 * them — a filename, in the one place that is meant to read as a shelf of
 * documents. So the slug is read as what it is a slug *of*: extension off,
 * hyphens and underscores back to spaces, words capitalised, small words left
 * small.
 *
 * On this wiki that reproduces every document's own `# ` heading exactly, all
 * twelve of them, which is the check that says the transform is right rather
 * than merely tidy. If a title ever diverges from its filename, the heading is
 * the thing to trust and the runner would have to read the first line of each
 * file to supply it — cheap at this size, and not worth the extra surface until
 * a document actually needs it.
 */
export function title(path: string): string {
  const base = path.slice(path.lastIndexOf('/') + 1);
  // The length guard is for a file named exactly `.md`, which would otherwise
  // come out as the empty string and render as a blank row.
  const stem = base.length > 3 && base.endsWith('.md') ? base.slice(0, -3) : base;
  const words = stem.split(/[-_\s]+/).filter(Boolean);
  if (!words.length) return stem;
  return words
    .map((word, i) => {
      const lower = word.toLowerCase();
      if (i > 0 && SMALL.has(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
}

/**
 * The same treatment along a whole path, for the heading over an open document:
 * `projects/ogham.md` reads as "Projects · Ogham".
 */
export function trail(path: string): string {
  return path
    .split('/')
    .filter(Boolean)
    .map(title)
    .join(' · ');
}

/**
 * Whether an entry is machinery rather than something to read.
 *
 * Three kinds, and the reasoning is the same for all three: this is a shelf of
 * documents, so anything that is not a document is furniture someone has to
 * look past. Dot-prefixed paths are tooling (`.claude`), non-markdown files are
 * tools rather than writing (`bin/sync.sh`), and `CLAUDE.md` is markdown
 * addressed to the agent rather than to a reader.
 *
 * This is presentation, not access control. The runner will still serve any of
 * these to anyone who asks for them by path — nothing here is secret, it is
 * just not what the wiki is for.
 */
const AGENT_FILES = new Set(['CLAUDE.md', 'CLAUDE.local.md', 'AGENTS.md']);

export function hidden(entry: WikiEntry): boolean {
  const parts = entry.path.split('/');
  if (parts.some(part => part.startsWith('.'))) return true;
  const base = parts[parts.length - 1];
  if (AGENT_FILES.has(base)) return true;
  return entry.type === 'file' && !base.endsWith('.md');
}

/**
 * Nest the runner's flat, path-sorted list.
 *
 * Sorting by path first means a directory is always seen before anything inside
 * it, so a parent is in the map by the time its children arrive and no second
 * pass is needed. An entry whose parent is missing — only reachable if the
 * runner's entry cap truncated the walk mid-directory — is attached at the root
 * rather than dropped, because a visible file in the wrong place is easier to
 * make sense of than one that silently isn't there.
 *
 * Machinery is filtered before nesting, and any directory left with nothing in
 * it afterwards goes too — otherwise hiding `bin/sync.sh` would leave an empty
 * `bin` to open and find nothing in, which is a worse row than the one it
 * replaced. A directory that was genuinely empty on disk is dropped by the same
 * rule; there is nothing behind it either way.
 */
export function buildTree(entries: WikiEntry[]): TreeNode[] {
  const byPath = new Map<string, TreeNode>();
  const roots: TreeNode[] = [];

  const sorted = [...entries]
    .filter(entry => !hidden(entry))
    .sort((a, b) => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0));
  for (const entry of sorted) {
    const node: TreeNode = {
      name: title(entry.path),
      path: entry.path,
      type: entry.type,
      last_commit: entry.last_commit,
      children: [],
    };
    if (entry.size !== undefined) node.size = entry.size;
    byPath.set(entry.path, node);

    const cut = entry.path.lastIndexOf('/');
    const parent = cut === -1 ? undefined : byPath.get(entry.path.slice(0, cut));
    if (parent) parent.children.push(node);
    else roots.push(node);
  }

  const kept = prune(roots);
  sortTree(kept);
  return kept;
}

/** Directories with no surviving descendant, removed depth-first. */
function prune(nodes: TreeNode[]): TreeNode[] {
  const kept: TreeNode[] = [];
  for (const node of nodes) {
    if (node.type === 'file') {
      kept.push(node);
      continue;
    }
    node.children = prune(node.children);
    if (node.children.length) kept.push(node);
  }
  return kept;
}

function sortTree(nodes: TreeNode[]): void {
  nodes.sort(compare);
  for (const node of nodes) sortTree(node.children);
}

function compare(a: TreeNode, b: TreeNode): number {
  // Files before directories, which is the opposite of a code explorer and the
  // right way round for a set of documents: it puts System and Ideas at the top
  // of the wiki, where they are the things you actually read, rather than below
  // three folders.
  if (a.type !== b.type) return a.type === 'file' ? -1 : 1;
  // `localeCompare`, because these are titles now rather than filenames, and a
  // codepoint sort would file "Éire" after "Zoo".
  return a.name.localeCompare(b.name);
}
